import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

export const ParentBillingSetupDuplicateCard = ({
  settings,
  activeChildCount,
  totalChildren,
  onFieldChange,
  onSave,
  saving,
}) => {
  const billing = settings.parentBillingSettings;
  const frequency = billing.billingFrequency;
  const monthlyFee = Number(billing.monthlyFeePerChild || 0);
  const yearlyFee = Number(billing.yearlyFeePerChild || 0);
  const feePerChild = Number(billing.feePerChild || 0);
  const billingDay = billing.billingDay || 1;
  const billingMode = billing.billingMode || "auto";
  const feePassthrough = billing.feePassthrough !== false;

  const enrolledChildrenCount = Number(totalChildren || 0);
  const potentialMonthlyRevenue = monthlyFee * enrolledChildrenCount;
  const potentialYearlyRevenue = yearlyFee * enrolledChildrenCount;
  const activeMonthlyRevenue = monthlyFee * activeChildCount;
  const activeYearlyRevenue = yearlyFee * activeChildCount;

  // Fee passthrough live calculation
  const base = feePerChild;
  const squareFee = +(base * 0.029 + 0.30).toFixed(2);
  const platformFee = 5.00;
  const parentPays = feePassthrough ? +(base + squareFee + platformFee).toFixed(2) : base;
  const centerNets = feePassthrough ? base : +(base - squareFee - platformFee).toFixed(2);

  const ordinalLabel = (d) => d === 1 ? "1st" : d === 2 ? "2nd" : d === 3 ? "3rd" : `${d}th`;

  return (
    <Card className="overflow-hidden border-2 border-emerald-200 bg-white shadow-xl" data-testid="parent-billing-setup-duplicate-card">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50">
        <CardTitle className="flex items-center gap-2 text-emerald-800">
          <DollarSign className="h-5 w-5" />
          Parent Billing Setup
        </CardTitle>
        <CardDescription className="text-emerald-900/80">
          Set up fees to charge parents for Parent Communication
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 p-6">
        {/* Charge Parents master toggle */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start justify-between gap-4" data-testid="charge-parents-toggle-row">
          <div>
            <p className="font-semibold text-slate-900">Charge parents for portal</p>
            <p className="text-xs text-slate-600 mt-1">When ON, parents are billed the fee below. When OFF, the portal is free for families even if fees are set.</p>
          </div>
          <Switch
            checked={!!billing.chargeParents}
            onCheckedChange={(v) => {
              if (v && monthlyFee <= 0 && yearlyFee <= 0) {
                toast.error("Set a Monthly or Yearly fee above $0 before turning on billing.");
                return;
              }
              onFieldChange("chargeParents", v);
            }}
            data-testid="duplicate-charge-parents-switch"
          />
        </div>

        {/* Monthly & Yearly fee inputs */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="duplicate-parent-billing-monthly-fee-card">
            <div className="flex items-center gap-2 text-emerald-700">
              <Mail className="h-5 w-5" />
              <p className="font-semibold">Monthly Parent Fee</p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xl text-slate-500">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={monthlyFee}
                onChange={(e) => onFieldChange("monthlyFeePerChild", e.target.value)}
                className="max-w-[120px]"
                data-testid="duplicate-parent-billing-monthly-fee-input"
              />
              <span className="text-slate-600">/ child / month</span>
            </div>
            <p className="mt-3 text-sm text-slate-500">Set to $0 for free</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="duplicate-parent-billing-yearly-fee-card">
            <div className="flex items-center gap-2 text-blue-700">
              <Phone className="h-5 w-5" />
              <p className="font-semibold">Yearly Parent Fee</p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xl text-slate-500">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={yearlyFee}
                onChange={(e) => onFieldChange("yearlyFeePerChild", e.target.value)}
                className="max-w-[120px]"
                data-testid="duplicate-parent-billing-yearly-fee-input"
              />
              <span className="text-slate-600">/ child / year</span>
            </div>
            <p className="mt-3 text-sm text-slate-500">Suggested: annual family access</p>
          </div>
        </div>

        {/* Billing Frequency */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="duplicate-parent-billing-frequency-card">
          <p className="font-semibold text-slate-900">Billing Frequency</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { value: "monthly", label: "Monthly" },
              { value: "yearly", label: "Annually" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onFieldChange("billingFrequency", option.value)}
                className={`rounded-xl border px-5 py-3 text-sm font-semibold transition ${
                  frequency === option.value
                    ? "border-emerald-400 bg-emerald-100 text-emerald-800 shadow-sm"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
                data-testid={`duplicate-parent-billing-frequency-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Estimated Revenue */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="duplicate-parent-billing-estimated-revenue-card">
          <p className="text-sm font-semibold text-emerald-800 mb-3">Estimated Revenue</p>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-xl font-bold text-emerald-700">{enrolledChildrenCount}</p>
                <p className="text-xs text-emerald-600">Children Enrolled</p>
              </div>
              <div>
                <p className="text-xl font-bold text-emerald-700">${potentialMonthlyRevenue.toFixed(2)}</p>
                <p className="text-xs text-emerald-600">Monthly (Potential)</p>
              </div>
              <div>
                <p className="text-xl font-bold text-emerald-700">${potentialYearlyRevenue.toFixed(2)}</p>
                <p className="text-xs text-emerald-600">Yearly (Potential)</p>
              </div>
            </div>
            <div className="border-t border-emerald-200 pt-3 grid grid-cols-3 gap-3">
              <div>
                <p className="text-xl font-bold text-emerald-700">{activeChildCount}</p>
                <p className="text-xs text-emerald-600">Children Activated</p>
              </div>
              <div>
                <p className="text-xl font-bold text-emerald-700">${activeMonthlyRevenue.toFixed(2)}</p>
                <p className="text-xs text-emerald-600">Monthly (Active)</p>
              </div>
              <div>
                <p className="text-xl font-bold text-emerald-700">${activeYearlyRevenue.toFixed(2)}</p>
                <p className="text-xs text-emerald-600">Yearly (Active)</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Billing Day of Month + Send Mode ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Billing Day */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-day-card">
            <p className="font-semibold text-slate-900 mb-3">Billing Day of Month</p>
            <select
              value={billingDay}
              onChange={(e) => onFieldChange("billingDay", parseInt(e.target.value))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="billing-day-select"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {ordinalLabel(d)} of the month
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Director gets reminder day before</p>
          </div>

          {/* Send Mode */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="send-mode-card">
            <p className="font-semibold text-slate-900 mb-3">Send Mode</p>
            <div className="space-y-2">
              {[
                { val: "auto", label: "Auto-Send", desc: "Sends automatically on billing day" },
                { val: "manual", label: "Manual Approval", desc: "Queue until you approve" },
              ].map((m) => (
                <label
                  key={m.val}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition ${
                    billingMode === m.val ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-blue-300"
                  }`}
                  data-testid={`send-mode-${m.val}`}
                >
                  <input
                    type="radio"
                    checked={billingMode === m.val}
                    onChange={() => onFieldChange("billingMode", m.val)}
                    className="accent-blue-600"
                  />
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{m.label}</p>
                    <p className="text-xs text-slate-400">{m.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Pass Fees to Parent ── */}
        <div className="rounded-xl border-2 border-blue-200 bg-white p-4 space-y-3" data-testid="fee-passthrough-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">Pass Fees to Parent</p>
              <p className="text-xs text-slate-500">Square processing fee + $5 platform fee</p>
            </div>
            <button
              type="button"
              onClick={() => onFieldChange("feePassthrough", !feePassthrough)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${feePassthrough ? "bg-blue-600" : "bg-slate-300"}`}
              data-testid="fee-passthrough-toggle"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${feePassthrough ? "translate-x-6" : "translate-x-1"}`}
              />
            </button>
          </div>
          <div className={`rounded-lg p-3 text-xs space-y-1 ${feePassthrough ? "bg-blue-50 border border-blue-200" : "bg-orange-50 border border-orange-200"}`}>
            <p className="font-bold text-slate-700 mb-2">
              {feePassthrough ? "✓ Pass fees to parent" : "✓ Center absorbs fees"} — Example at ${base.toFixed(2)}/child:
            </p>
            <div className="space-y-1 font-mono text-slate-600">
              <div className="flex justify-between">
                <span>Your SMS fee</span>
                <span>${base.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between ${feePassthrough ? "text-orange-600" : "text-slate-400 line-through"}`}>
                <span>Square processing (2.9%+$0.30)</span>
                <span>+${squareFee.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between ${feePassthrough ? "text-orange-600" : "text-slate-400 line-through"}`}>
                <span>Platform fee</span>
                <span>+$5.00</span>
              </div>
              <div className="border-t pt-1 mt-1 flex justify-between font-bold text-slate-800">
                <span>Parent pays</span>
                <span>${parentPays.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between font-bold ${centerNets < 0 ? "text-red-600" : "text-green-700"}`}>
                <span>You receive</span>
                <span>${centerNets.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
          data-testid="duplicate-parent-billing-save-button"
        >
          {saving ? "Saving..." : "Save Billing Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};
